import { ChangeEvent, useCallback, useState } from "react";

export type RecordImage = {
  id: number;
  slot: number;
  fileName: string;
  contentType: string;
  byteSize: number;
};

export type PhotoSlot =
  | { kind: "empty" }
  | { kind: "existing"; imageId: number; fileName: string }
  | { kind: "new"; file: File };

export const PHOTO_SLOTS = [0, 1];

const EMPTY_SLOTS: PhotoSlot[] = [{ kind: "empty" }, { kind: "empty" }];
const MAX_IMAGE_EDGE = 1600;
const MAX_IMAGE_BYTES = 6 * 1024 * 1024;
const RECOMPRESS_ABOVE_BYTES = 400 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Photos come straight off a phone camera, so they are scaled down before
 * upload — the archive stays small enough to download over the warehouse wifi.
 */
async function prepareImage(file: File) {
  if (file.size <= RECOMPRESS_ABOVE_BYTES && ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas is unavailable.");
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.78),
    );
    if (!blob) throw new Error("Encoding failed.");

    const baseName = file.name.replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

/**
 * Downscales and checks one picked file. Shared with the HW check tasks, where
 * photos are picked per task line instead of per form.
 */
export async function preparePhotoUpload(
  file: File,
): Promise<{ file: File; error?: undefined } | { file?: undefined; error: string }> {
  const prepared = await prepareImage(file);

  if (!ALLOWED_IMAGE_TYPES.includes(prepared.type)) {
    return { error: "Csak JPEG, PNG vagy WEBP kép tölthető fel." };
  }
  if (prepared.size > MAX_IMAGE_BYTES) {
    return { error: "A kép túl nagy, legfeljebb 6 MB tölthető fel." };
  }

  return { file: prepared };
}

/** Slot state and downscaling for a two-photo form. */
export function usePhotoSlots(onError: (message: string) => void) {
  const [photoSlots, setPhotoSlots] = useState<PhotoSlot[]>(EMPTY_SLOTS);

  const resetPhotos = useCallback(() => setPhotoSlots(EMPTY_SLOTS), []);

  const loadPhotos = useCallback((images: RecordImage[]) => {
    setPhotoSlots(
      PHOTO_SLOTS.map((index) => {
        const image = images.find((item) => item.slot === index + 1);
        return image
          ? ({ kind: "existing", imageId: image.id, fileName: image.fileName } as PhotoSlot)
          : ({ kind: "empty" } as PhotoSlot);
      }),
    );
  }, []);

  const pickPhoto = useCallback(
    async (index: number, event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      const prepared = await preparePhotoUpload(file);
      if (prepared.error) {
        onError(prepared.error);
        return;
      }

      setPhotoSlots((current) =>
        current.map((slot, slotIndex) =>
          slotIndex === index ? { kind: "new", file: prepared.file } : slot,
        ),
      );
    },
    [onError],
  );

  const clearPhoto = useCallback((index: number) => {
    setPhotoSlots((current) =>
      current.map((slot, slotIndex) =>
        slotIndex === index ? { kind: "empty" } : slot,
      ),
    );
  }, []);

  return { photoSlots, resetPhotos, loadPhotos, pickPhoto, clearPhoto };
}

/** Mirrors the slot intents the API expects: keep, empty or replace. */
export function appendPhotoSlots(payload: FormData, slots: PhotoSlot[]) {
  slots.forEach((slot, index) => {
    const field = `image${index + 1}`;
    if (slot.kind === "new") {
      payload.set(`${field}Action`, "replace");
      payload.set(field, slot.file, slot.file.name);
      return;
    }
    payload.set(`${field}Action`, slot.kind === "existing" ? "keep" : "empty");
  });
}

// --- Unlimited-count photos (DHG only) -----------------------------
//
// The fixed two-slot model above (PhotoSlot / usePhotoSlots / PhotoFields)
// stays exactly as it is for Deletion Request and HW check photos. DHG
// records instead get as many photos as the operator wants, so they use
// this separate, list-based variant.

function localId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export type UnlimitedPhoto =
  | { kind: "existing"; imageId: number; fileName: string }
  | { kind: "new"; file: File; localId: string };

/** Photo list state for a form that accepts any number of photos. */
export function useUnlimitedPhotos(
  onError: (message: string) => void,
  compress: (
    file: File,
  ) => Promise<{ file: File; error?: undefined } | { file?: undefined; error: string }> = preparePhotoUpload,
) {
  const [photos, setPhotos] = useState<UnlimitedPhoto[]>([]);

  const resetPhotos = useCallback(() => setPhotos([]), []);

  const loadPhotos = useCallback((images: RecordImage[]) => {
    setPhotos(
      images.map(
        (image) =>
          ({ kind: "existing", imageId: image.id, fileName: image.fileName }) as UnlimitedPhoto,
      ),
    );
  }, []);

  const addPhotos = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;

      const prepared: UnlimitedPhoto[] = [];
      for (const file of Array.from(fileList)) {
        const result = await compress(file);
        if (result.error) {
          onError(result.error);
          continue;
        }
        prepared.push({ kind: "new", file: result.file, localId: localId() });
      }
      if (prepared.length > 0) {
        setPhotos((current) => [...current, ...prepared]);
      }
    },
    [onError, compress],
  );

  const removePhoto = useCallback((target: UnlimitedPhoto) => {
    setPhotos((current) =>
      current.filter((photo) => {
        if (photo.kind === "existing" && target.kind === "existing") {
          return photo.imageId !== target.imageId;
        }
        if (photo.kind === "new" && target.kind === "new") {
          return photo.localId !== target.localId;
        }
        return true;
      }),
    );
  }, []);

  return { photos, resetPhotos, loadPhotos, addPhotos, removePhoto };
}

/**
 * Sends the kept existing image ids plus any newly picked files. The API
 * deletes whichever existing images are NOT in `keepImageIds`.
 */
export function appendUnlimitedPhotos(payload: FormData, photos: UnlimitedPhoto[]) {
  const keepImageIds = photos
    .filter((photo): photo is Extract<UnlimitedPhoto, { kind: "existing" }> => photo.kind === "existing")
    .map((photo) => photo.imageId);

  payload.set("keepImageIds", JSON.stringify(keepImageIds));

  for (const photo of photos) {
    if (photo.kind === "new") {
      payload.append("newPhotos", photo.file, photo.file.name);
    }
  }
}

/** Add-as-many-as-you-want photo picker with a thumbnail-free file list and a "+" tile. */
export function UnlimitedPhotoFields({
  photos,
  onAdd,
  onRemove,
}: {
  photos: UnlimitedPhoto[];
  onAdd: (files: FileList | null) => void;
  onRemove: (photo: UnlimitedPhoto) => void;
}) {
  return (
    <div className="field field-wide photo-fields">
      <p className="photo-warning">Upload only for request!</p>
      {photos.map((photo) => {
        const key = photo.kind === "existing" ? `existing-${photo.imageId}` : `new-${photo.localId}`;
        const fileName = photo.kind === "existing" ? photo.fileName : photo.file.name;

        return (
          <div className="photo-field" key={key}>
            <span className="photo-name">{fileName}</span>
            <button
              className="photo-clear"
              type="button"
              onClick={() => onRemove(photo)}
              title="Remove photo"
              aria-label="Remove photo"
            >
              ✕
            </button>
          </div>
        );
      })}
      <label className="photo-input">
        <span className="photo-placeholder">+ add photo</span>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(event) => {
            onAdd(event.target.files);
            event.target.value = "";
          }}
        />
      </label>
    </div>
  );
}

/**
 * Two plain upload fields instead of preview tiles — photos are the exception
 * on this form, so the warning above them is what should catch the eye.
 */
export function PhotoFields({
  slots,
  onPick,
  onClear,
}: {
  slots: PhotoSlot[];
  onPick: (index: number, event: ChangeEvent<HTMLInputElement>) => void;
  onClear: (index: number) => void;
}) {
  return (
    <div className="field field-wide photo-fields">
      <p className="photo-warning">Upload only for request!</p>
      {slots.map((slot, index) => {
        const fileName =
          slot.kind === "new"
            ? slot.file.name
            : slot.kind === "existing"
              ? slot.fileName
              : "";

        return (
          <div className="photo-field" key={index}>
            <label className="photo-input">
              <span className={fileName ? "photo-name" : "photo-placeholder"}>
                {fileName || "upload photo"}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => onPick(index, event)}
              />
            </label>
            {fileName && (
              <button
                className="photo-clear"
                type="button"
                onClick={() => onClear(index)}
                title={`Remove photo ${index + 1}`}
                aria-label={`Remove photo ${index + 1}`}
              >
                ✕
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
